import type React from 'react';
import type { RefObject } from 'react';
import { useMemo } from 'react';

import { useLanguagesEventSourcing } from 'stream/languages/useLanguagesEventSourcing';

import type { InputWrapperProps } from '@polyai/ui/components/atoms/InputWrapper';
import InputWrapper from '@polyai/ui/components/atoms/InputWrapper';

import type { RichTextInputV2Props } from 'components/molecules/RichTextInputV2';
import NewRichTextInputV2 from 'components/molecules/RichTextInputV2';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useIsDemoAccount } from 'hooks/useIsDemoAccount';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import type { ActionPickerConfig } from '../LexicalEditor/plugins/ActionPicker/types';
import { MenuOptions } from '../LexicalEditor/plugins/ActionPicker/types';
import * as Styled from './RichTextInput.styled';
import type { Options } from './utils';
import { getOptions, replaceIds } from './utils';

export type RichTextOverrideConfig = {
  handoff?: boolean;
  functions?: boolean;
  sms?: boolean;
  flows?: boolean;
  flowEdit?: boolean;
  variantAttributes?: boolean;
  entities?: boolean;
  variables?: boolean;
  integrations?: boolean;
  translations?: boolean;
};

type RichTextInputProps = {
  readOnly?: boolean;
  preview?: boolean;
  loading?: boolean;
  value: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isInFlowPage?: boolean;
  disabled?: boolean;
  placeholder?: string;
  deploymentId?: string;
  isActionTagStatic?: boolean;
  actionTagCreateDisabled?: boolean;
  config?: RichTextOverrideConfig;
  readonly?: boolean;
  staticPlusButtonPosition?: boolean;
  referencesField?: string;
  resetReferences?: boolean;
  customContext?: Record<string, any>;
  height?: number;
  inputRef?: RefObject<HTMLElement | null>;
  singleLine?: boolean;
  inputWrapperClassName?: string;
} & InputWrapperProps;

const CONFIG_MAP: Record<keyof RichTextOverrideConfig, MenuOptions[]> = {
  handoff: [MenuOptions.HANDOFF],
  functions: [MenuOptions.FUNCTIONS],
  sms: [MenuOptions.SMS],
  flows: [MenuOptions.FLOWS],
  flowEdit: [MenuOptions.STEPS, MenuOptions.TRANSITION_FUNCTION],
  variantAttributes: [MenuOptions.VARIANT_ATTRIBUTES],
  entities: [MenuOptions.ENTITIES],
  variables: [MenuOptions.VARIABLES],
  integrations: [MenuOptions.INTEGRATIONS],
  translations: [MenuOptions.TRANSLATIONS],
};

export const DEFAULT_CONFIG: RichTextOverrideConfig = {
  handoff: true,
  functions: true,
  sms: true,
  flows: true,
  flowEdit: false,
  variantAttributes: true,
  entities: false,
  variables: false,
  integrations: false,
  translations: false,
};

const getConfigValue = (
  config: RichTextOverrideConfig | undefined,
  key: keyof RichTextOverrideConfig,
) => !config || !!config[key];

export const NewRichTextInput: React.FC<RichTextInputProps> = ({
  readOnly = false,
  preview = false,
  loading = false,
  value = '',
  onChange,
  onBlur,
  disabled,
  placeholder,
  isActionTagStatic,
  actionTagCreateDisabled,
  config,
  readonly = false,
  staticPlusButtonPosition,
  referencesField,
  resetReferences,
  customContext,
  height,
  inputRef,
  singleLine,
  inputWrapperClassName,
  ...props
}) => {
  const { isFeatureOn } = useIsFeatureOn();
  const { isDemoAccount } = useIsDemoAccount();
  const { isProjectResourceEnabled } = useProjectResource();
  const isVariablesFeatureEnabled = isFeatureOn('variables');
  const isIntegrationsFeatureEnabled =
    isDemoAccount || isFeatureOn('paragon_integrations');
  const { additionalLanguages } = useLanguagesEventSourcing();
  const isTranslationsFeatureEnabled = isFeatureOn('multilingual_config');

  const v2Config: RichTextInputV2Props['config'] = useMemo(() => {
    const v2Config = config ?? {
      ...DEFAULT_CONFIG,
      variables: isVariablesFeatureEnabled,
      integrations: isIntegrationsFeatureEnabled,
      translations: isTranslationsFeatureEnabled,
    };
    return {
      actionTag: Object.keys(v2Config)
        .filter(key => v2Config[key as keyof RichTextOverrideConfig])
        .reduce((config, key) => {
          const menuOptions = CONFIG_MAP[key as keyof RichTextOverrideConfig];
          for (const option of menuOptions) {
            const allowCreate = actionTagCreateDisabled
              ? false
              : option === MenuOptions.FLOWS
                ? !v2Config.flowEdit
                : true;
            config[option] = {
              select: true,
              create: allowCreate,
            };
          }
          return config;
        }, {} as ActionPickerConfig),
      isActionTagStatic,
      customContext,
    };
  }, [
    config,
    isActionTagStatic,
    actionTagCreateDisabled,
    customContext,
    isVariablesFeatureEnabled,
    isIntegrationsFeatureEnabled,
    isTranslationsFeatureEnabled,
  ]);

  const isHandoffFeatureOn =
    isFeatureOn('call_handoff') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.HANDOFFS) &&
    getConfigValue(config, 'handoff');
  const isFunctionsFeatureOn =
    isFeatureOn('function_calls') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.FUNCTIONS) &&
    getConfigValue(config, 'functions');
  const isSmsFeatureOn =
    isFeatureOn('sms_templates') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.SMS) &&
    getConfigValue(config, 'sms');
  const isFlowsFeatureOn =
    isProjectResourceEnabled(PROJECT_RESOURCE.FLOWS) &&
    getConfigValue(config, 'flows');
  const isVariantAttributesFeatureOn =
    isFeatureOn('jupiter_variant_attributes') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.VARIANT_MANAGEMENT) &&
    getConfigValue(config, 'variantAttributes');
  const isVariablesFeatureOn =
    isFeatureOn('variables') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.FUNCTIONS) &&
    getConfigValue(config, 'variables');
  const isTranslationsFeatureOn =
    isFeatureOn('multilingual_config') &&
    additionalLanguages.length > 0 &&
    getConfigValue(config, 'translations');

  const isAnyFeatureOn =
    isSmsFeatureOn ||
    isFlowsFeatureOn ||
    isFunctionsFeatureOn ||
    isHandoffFeatureOn ||
    isVariantAttributesFeatureOn ||
    isVariablesFeatureOn ||
    isTranslationsFeatureOn;

  const hasActionTagRefs =
    /\{\{(fn:|ho:|ft:|twilio_sms:|attr:|entity:|vrbl:|integration:|tn:)/.test(
      value,
    );

  if (!isAnyFeatureOn && !hasActionTagRefs) {
    return (
      <Styled.TextInput
        {...props}
        ref={
          inputRef as RefObject<HTMLInputElement | HTMLTextAreaElement | null>
        }
        disabled={disabled}
        placeholder={placeholder}
        readOnly={readOnly || readonly}
        style={{ minHeight: height }}
        value={value}
        fullWidth
        textArea
        onBlur={onBlur}
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  return (
    <InputWrapper {...props} labelAsDiv>
      <NewRichTextInputV2
        config={v2Config}
        data-dd-action={props['data-dd-action']}
        data-test-id="knowledge-base-input"
        disabled={disabled}
        height={height}
        inputRef={inputRef as RefObject<HTMLDivElement | null>}
        loading={loading}
        placeholder={placeholder}
        preview={preview}
        readOnly={readOnly || readonly}
        referencesField={referencesField}
        resetReferences={resetReferences}
        role="input"
        singleLine={singleLine}
        staticPlusButtonPosition={staticPlusButtonPosition}
        value={value}
        inputWrapperClassName={inputWrapperClassName}
        onBlur={onBlur}
        onChange={onChange}
      />
    </InputWrapper>
  );
};

export { getOptions, replaceIds };
export type { Options };

export default NewRichTextInput;
